/**
 * Blog capture handler — Klaviyo
 *
 * Intercepts all blog CRO form submissions (scroll banner, sticky sidebar,
 * exit-intent), POSTs to the Klaviyo Client Subscriptions API, and fires GA4
 * events + sets the frequency-capping cookie.
 *
 * Replaces brevo-subscribe-handler.js. Brevo is retired. The request shape is
 * copied from sections/persona-popup.liquid so both capture surfaces speak to
 * Klaviyo identically.
 *
 * No API key: the client endpoint authenticates with the PUBLIC company_id
 * alone. Nothing secret is injected into the page.
 *
 * Properties are written with snapshot() — lowercase — deliberately. The
 * UPPERCASE_SNAKE casing was a Brevo attribute convention; blog profiles must
 * carry the same lowercase property names as popup profiles or the two cannot
 * be segmented together in Klaviyo.
 */
(function () {
  var forms = document.querySelectorAll('form[data-source]');
  if (!forms.length) return;

  var COMPANY_ID = 'SwYVpD';
  var LIST_ID    = 'RrTRu4';        // "Viori Blog Captures" — all three forms
  var REVISION   = '2025-04-15';

  // Outbound calls get a hard 2500ms ceiling. A marketing POST must never be
  // the reason a visitor sits watching a disabled button.
  var CALL_TIMEOUT_MS = 2500;
  var RETRY_DELAY_MS  = 1000;

  function withTimeout(promise, label) {
    var timer;
    var timeout = new Promise(function (_, reject) {
      timer = setTimeout(function () {
        reject(new Error(label + ' timed out after ' + CALL_TIMEOUT_MS + 'ms'));
      }, CALL_TIMEOUT_MS);
    });
    return Promise.race([promise, timeout]).finally(function () { clearTimeout(timer); });
  }

  // Same implementation as sections/persona-popup.liquid, so a number entered
  // on either surface normalizes identically. Returns null for anything it
  // cannot make sense of — callers must treat null as "no phone", never as an
  // error: the phone is optional and must not cost us the email.
  function normalizePhone(v) {
    if (!v) return null;
    var digits = String(v).replace(/\D/g, '');
    if (digits.length === 10) return '+1' + digits;
    if (digits.length === 11 && digits[0] === '1') return '+' + digits;
    if (digits.length >= 8 && digits.length <= 15) return '+' + digits;
    return null;
  }

  // ── Payload ───────────────────────────────────────────────────────────────
  function put(out, key, val) {
    if (val === null || val === undefined) return;
    var s = String(val);
    if (!s) return;                       // never send empty strings
    out[key] = s;
  }

  function buildProperties(source, formData) {
    // Attribution first; the hand-authored capture_* keys below win on any
    // collision. VioriAttribution is loaded site-wide from theme.liquid, but
    // guard anyway so a page without it still submits, just unattributed.
    var attribution = {};
    try {
      if (window.VioriAttribution && typeof window.VioriAttribution.snapshot === 'function') {
        attribution = window.VioriAttribution.snapshot() || {};
      }
    } catch (ex) {
      console.warn('[blog-subscribe] attribution snapshot unavailable:', ex);
    }

    var merged = Object.assign({}, attribution, {
      capture_surface: 'blog_' + source,
      blog_post: formData.get('post_slug') || undefined,
      blog_theme: formData.get('post_theme') || undefined,
      content_upgrade_id: formData.get('content_upgrade_id') || undefined,
      capture_page_path: window.location.pathname
    });

    // Drop empties — snapshot() already omits blanks, but the form fields above
    // and snapshot's null viori_session_id still need filtering.
    var out = {};
    for (var k in merged) {
      if (merged.hasOwnProperty(k)) put(out, k, merged[k]);
    }
    return out;
  }

  function buildPayload(source, email, phone, formData) {
    var attrs = {
      email: email,
      subscriptions: { email: { marketing: { consent: 'SUBSCRIBED' } } }
    };

    // Phone is captured as PROFILE DATA ONLY — no SMS marketing consent, no SMS
    // list. These forms show consent copy but have no consent checkbox, so
    // subscribing the number would be a TCPA problem. Storing it lets the
    // number be used once a real checkbox exists.
    //
    // Already normalized to E.164 by the caller, or null. Null means the field
    // was blank or unparseable, and the key is simply omitted — an unusable
    // optional phone must never turn into a 400 that costs us the email.
    if (phone) attrs.phone_number = phone;

    var props = buildProperties(source, formData);
    if (Object.keys(props).length) attrs.properties = props;

    return {
      data: {
        type: 'subscription',
        attributes: {
          custom_source: 'Blog Capture (' + source + ')',
          profile: { data: { type: 'profile', attributes: attrs } }
        },
        relationships: { list: { data: { type: 'list', id: LIST_ID } } }
      }
    };
  }

  // ── Transport ─────────────────────────────────────────────────────────────
  // Resolves only on a confirmed 2xx. A 400 is a deterministic validation
  // failure (bad email) — a second identical request cannot change it, so it is
  // surfaced immediately rather than retried.
  function ValidationError(message) { this.name = 'ValidationError'; this.message = message; }
  ValidationError.prototype = Object.create(Error.prototype);

  function postToKlaviyo(payload) {
    return withTimeout(fetch('https://a.klaviyo.com/client/subscriptions/?company_id=' + encodeURIComponent(COMPANY_ID), {
      method: 'POST',
      headers: {
        'revision': REVISION,
        'content-type': 'application/json'
      },
      body: JSON.stringify(payload)
    }).then(function (res) {
      if (res.status === 202 || res.status === 200 || res.status === 204) return res;
      if (res.status === 400) {
        return res.text().then(function () {
          throw new ValidationError('Please check your email address.');
        });
      }
      return res.text().then(function (t) {
        throw new Error('Klaviyo ' + res.status + ': ' + t.slice(0, 240));
      });
    }), 'Klaviyo subscription POST');
  }

  function submitToKlaviyo(payload) {
    return postToKlaviyo(payload).catch(function (err) {
      if (err instanceof ValidationError) throw err;   // no retry on a 400
      console.warn('[blog-subscribe] attempt 1 failed — retrying once in ' + RETRY_DELAY_MS + 'ms:', err);
      return new Promise(function (resolve) { setTimeout(resolve, RETRY_DELAY_MS); })
        .then(function () { return postToKlaviyo(payload); });
    });
  }

  // ── UI helpers ────────────────────────────────────────────────────────────
  function showError(form, message) {
    var errDiv = form.querySelector('.blog-form-error') || document.createElement('div');
    errDiv.className = 'blog-form-error';
    errDiv.textContent = message;
    errDiv.style.cssText = 'color:#D90073;font-size:13px;margin-top:8px;';
    form.appendChild(errDiv);
  }

  // ── Bind ──────────────────────────────────────────────────────────────────
  forms.forEach(function (form) {
    form.addEventListener('submit', async function (e) {
      e.preventDefault();

      var formData  = new FormData(form);
      var source    = form.getAttribute('data-source');
      var email     = formData.get('email');
      var phone     = normalizePhone(formData.get('sms'));
      var upgradeId = formData.get('content_upgrade_id');

      var btn = form.querySelector('button[type="submit"]');
      var originalText = btn ? btn.textContent : '';
      if (btn) { btn.disabled = true; btn.textContent = 'Sending...'; }

      try {
        // Fails closed: anything other than a confirmed 2xx throws, and the
        // catch below skips the GA4 events, the viori_subscriber cookie, the
        // content-upgrade delivery and the success UI entirely. The form is
        // left intact so the entered value is retained.
        await submitToKlaviyo(buildPayload(source, email, phone, formData));

        // GA4 events
        if (window.gtag) {
          gtag('event', 'email_capture', {
            source: source,
            post_theme: formData.get('post_theme') || '',
            post_slug: formData.get('post_slug') || ''
          });
          if (phone) {
            gtag('event', 'sms_capture', {
              source: source,
              post_theme: formData.get('post_theme') || '',
              post_slug: formData.get('post_slug') || ''
            });
          }
          if (upgradeId) {
            gtag('event', 'content_upgrade_request', {
              source: source,
              upgrade_id: upgradeId,
              post_slug: formData.get('post_slug') || ''
            });
          }
        }

        // Mark session engaged (frequency cap)
        try { sessionStorage.setItem('viori_cta_engaged_session', 'true'); } catch (ex) {}

        // Subscriber cookie suppresses future exit-intent (90 days)
        document.cookie = 'viori_subscriber=1;max-age=7776000;path=/;SameSite=Lax';

        // Success UI
        form.innerHTML = '<div class="blog-form-success"><strong>Check your inbox.</strong> Your guide is on its way.</div>';

      } catch (err) {
        if (btn) { btn.disabled = false; btn.textContent = originalText; }
        if (err && err.name === 'ValidationError') {
          showError(form, err.message);
        } else {
          console.warn('[blog-subscribe] capture failed after retry:', err);
          showError(form, 'Something went wrong. Please try again.');
        }
      }
    });
  });
})();
